import { memo, useCallback, useImperativeHandle, useState } from 'react'
import PropTypes from 'prop-types'

import { Checkbox, DatePicker, Dropdown, FormRow, Label, TimeField, WithTooltip } from 'basic-components'
import { Info } from 'basic-components/icons'
import TimeService from 'dashboard-services/services/TimeService'

import { When } from 'react-if'

import './DataRange.scss'

const DataRange = ({ defaultDataStart = undefined, defaultDataEnd = undefined, defaultTimezone = "UTC", timezoneFixed = false, ref }) => {
  const [showDataRange, setShowDataRange] = useState(() => defaultDataStart !== undefined || defaultDataEnd !== undefined),
        [dataStartDate, setDataStartDate] = useState(() => defaultDataStart ? String(defaultDataStart).split("T")[0] : undefined),
        [dataEndDate, setDataEndDate] = useState(() => defaultDataEnd ? String(defaultDataEnd).split("T")[0] : undefined),
        [dataStartTime, setDataStartTime] = useState(() => defaultDataStart ? String(defaultDataStart).split("T")[1]?.split(".")[0] : undefined),
        [dataEndTime, setDataEndTime] = useState(() => defaultDataEnd ? String(defaultDataEnd).split("T")[1]?.split(".")[0] : undefined),
        [timezone, setTimezone] = useState(() => TimeService.TIME_ZONES.find(p => p.value === defaultTimezone)),
        onChangeTimezone = useCallback((_, { data }) => setTimezone(data), [])

  useImperativeHandle(ref, () => ({
    getDataStart: () => {
      if(showDataRange) {
        return dataStartDate ? `${dataStartDate}T${dataStartTime ? TimeService.getTimeWithSeconds(dataStartTime) : "00:00:00"}` : undefined
      }
      return undefined
    },
    getDataEnd: () => {
      if(showDataRange) {
        return dataEndDate ? `${dataEndDate}T${dataEndTime ? TimeService.getTimeWithSeconds(dataEndTime) : "00:00:00"}` : undefined
      }
      return undefined
    },
    getTimezone: () => showDataRange ? timezone?.value : undefined
  }))

  return (
    <div className="data-lake__group-dialog__edit-access__data-range">
      <div className="data-lake__group-dialog__edit-access__data-range__text">
        <Checkbox
            checked={showDataRange}
            onChange={setShowDataRange}
        >
          Give access to files from a specific period
        </Checkbox>
        <WithTooltip
            data-place="right"
            text="It works only for files with proper metadata set during upload."
        >
          <div className="data-lake__group-dialog__edit-access__data-range__text__info">
            <Info
                color="blue-bright"
                height={16}
                width={16}
            />
          </div>
        </WithTooltip>
      </div>
      <When condition={showDataRange}>
        {() => (
          <div className="data-lake__group-dialog__edit-access__data-range__section">
            <FormRow isVertical>
              <Label noError>From</Label>
              <div className="data-lake__group-dialog__edit-access__data-range__section__inputs">
                <DatePicker
                    defaultValue={dataStartDate}
                    fixed
                    isPortal
                    name="dataStartDate"
                    noError
                    onChange={setDataStartDate}
                    variant="border"
                    zIndex={9999}
                />
                <TimeField
                    defaultValue={dataStartTime}
                    name="dataStartTime"
                    noError
                    onChange={setDataStartTime}
                    variant="border"
                />
              </div>
            </FormRow>
            <FormRow isVertical>
              <Label noError>To</Label>
              <div className="data-lake__group-dialog__edit-access__data-range__section__inputs">
                <DatePicker
                    defaultValue={dataEndDate}
                    fixed
                    isPortal
                    name="dataEndDate"
                    noError
                    onChange={setDataEndDate}
                    variant="border"
                    zIndex={9999}
                />
                <TimeField
                    defaultValue={dataEndTime}
                    name="dataEndTime"
                    noError
                    onChange={setDataEndTime}
                    variant="border"
                />
              </div>
            </FormRow>
            <FormRow isVertical>
              <Label noError>Timezone</Label>
              <Dropdown
                  defaultValue={timezone}
                  fixed={timezoneFixed}
                  name="timezone"
                  noError
                  onChange={onChangeTimezone}
                  options={TimeService.TIME_ZONES}
                  variant="border"
              />
            </FormRow>
          </div>
        )}
      </When>
    </div>
  )
}

DataRange.propTypes = {
  defaultDataEnd: PropTypes.string,
  defaultDataStart: PropTypes.string,
  defaultTimezone: PropTypes.string,
  timezoneFixed: PropTypes.bool
}

export default memo(DataRange)
